import { default as React } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import { Box } from '@material-ui/core';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { makeStyles } from '@mui/styles';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PortIcon from '@mui/icons-material/SettingsEthernet';
import StorageIcon from '@mui/icons-material/Storage';
import FolderIcon from '@mui/icons-material/Folder';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { useNavigate } from 'react-router-dom';
import { UnregisterAppButton } from './UnregisterAppButton';
import { ViewPodLogsButton } from './ViewPodLogsButton';
import {
  useCurrentUserEmail,
  isUserAuthorizedForEntity,
  useSkyStageAdminEmails,
  useMicroappEnvironmentStatus,
} from './hooks';

const MICROAPP_ANNOTATION_KEY = 'skystage.wellsky.com/type';
const MICROAPP_ANNOTATION_VALUE = 'microapp';

// GCP Workload Identity Pool configuration for Quick Projects
// Dev: plateng-dev-app-wsky (project 880599546964)
// Prod: plateng-prod-app-wsky (project 791409896686)
const WORKLOAD_IDENTITY_POOLS = {
  dev: 'principal://iam.googleapis.com/projects/880599546964/locations/global/workloadIdentityPools/plateng-dev-app-wsky.svc.id.goog',
  prod: 'principal://iam.googleapis.com/projects/791409896686/locations/global/workloadIdentityPools/plateng-prod-app-wsky.svc.id.goog',
};

const useStyles = makeStyles(theme => ({
  infoRow: {
    display: 'flex',
    marginBottom: theme.spacing(1.5),
    alignItems: 'flex-start',
  },
  label: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontWeight: 'bold',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    width: '180px',
    flexShrink: 0,
    paddingTop: '2px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  value: {
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    flex: 1,
    wordBreak: 'break-word',
  },
  chip: {
    height: '20px',
    fontSize: '0.75rem',
    marginRight: theme.spacing(0.5),
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
}));

export const MicroappConfigCard = ({ variant }: { variant?: 'gridItem' }) => {
  const { entity } = useEntity();
  const classes = useStyles();
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();
  const navigate = useNavigate();
  // Detect environment from the browser's current hostname, not app config.
  // This ensures correct WIF pool regardless of how APP_BASE_URL is configured:
  // - localhost, 127.0.0.1, *.dev.wellsky.cloud → dev WIF pool (plateng-dev-app-wsky)
  // - skystage.wellsky.cloud (no .dev) → prod WIF pool (plateng-prod-app-wsky)
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const isDevEnvironment =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.includes('.dev.wellsky.cloud');
  const workloadIdentityPool = isDevEnvironment
    ? WORKLOAD_IDENTITY_POOLS.dev
    : WORKLOAD_IDENTITY_POOLS.prod;

  // Check if this is a microapp
  const isMicroapp =
    entity.metadata.annotations?.[MICROAPP_ANNOTATION_KEY] ===
    MICROAPP_ANNOTATION_VALUE;

  // Authorized if the user owns/created the app or is a SkyStage admin.
  // Gates the Application Configuration panel, Unregister, and View Pod Logs.
  const isAuthorized = isUserAuthorizedForEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );

  // Rename for clarity (backward compatibility)
  const isCreator = isAuthorized;

  // IDP-983: Fetch live environment status to determine which WIF principals to show
  const { status: envStatus } = useMicroappEnvironmentStatus(
    entity.metadata.name,
  );
  const stagingDeployed =
    envStatus?.staging?.deployed && !envStatus?.staging?.scaledToZero;
  const productionDeployed = envStatus?.production?.deployed;

  // Get microapp configuration details for display
  const getConfigDetails = () => {
    const annotations = entity.metadata.annotations || {};
    const appType = annotations['microapp/appType'];
    const visibility = annotations['microapp/visibility'];
    const requiresVPN = annotations['microapp/requiresVPN'] === 'true';
    const port = annotations['microapp/port'];
    const hasPersistentStorage =
      annotations['microapp/persistentStorage'] === 'true';
    const storageSize = annotations['microapp/storageSize'];
    const serviceAccount = annotations['microapp/serviceAccount'];
    const owners = annotations['microapp/owners'];
    const createdBy = annotations['microapp/createdBy'];
    const createdAt = annotations['microapp/createdAt'];

    // IDP-983: Compute environment-specific WIF principals based on live status.
    // The annotation 'kubernetes.io/namespace' holds the current entity's namespace,
    // which could be staging (qp-{app}-staging-ns) or production (qp-{app}-ns).
    // We derive both namespaces from the app name to ensure correctness regardless
    // of which environment the entity currently points to.
    const appName = entity.metadata.name;
    const productionNamespace = `qp-${appName}-ns`;
    const stagingNamespace = `qp-${appName}-staging-ns`;
    const k8sServiceAccount = serviceAccount || `qp-${appName}-sa`;

    let productionPrincipal: string | undefined;
    let stagingPrincipal: string | undefined;
    if (productionDeployed) {
      productionPrincipal = `${workloadIdentityPool}/subject/ns/${productionNamespace}/sa/${k8sServiceAccount}`;
    }
    if (stagingDeployed) {
      stagingPrincipal = `${workloadIdentityPool}/subject/ns/${stagingNamespace}/sa/${k8sServiceAccount}`;
    }

    return {
      appType,
      visibility,
      requiresVPN,
      port,
      serviceAccount,
      productionPrincipal,
      stagingPrincipal,
      hasPersistentStorage,
      storageSize,
      owners,
      createdBy,
      createdAt,
    };
  };

  const configDetails = getConfigDetails();

  const showConfigInfo =
    isMicroapp &&
    isCreator &&
    (configDetails.appType ||
      configDetails.visibility ||
      configDetails.requiresVPN ||
      configDetails.port ||
      configDetails.productionPrincipal ||
      configDetails.stagingPrincipal ||
      configDetails.hasPersistentStorage);

  // Show unregister/delete button for both Registered and Hosted apps
  const showUnregisterButton = isMicroapp && isCreator;

  // Show View in ArgoCD button only for Hosted apps (they have ArgoCD deployments)
  const showViewInArgoCDButton =
    isMicroapp && isCreator && configDetails.appType === 'Hosted';

  // Don't render if not a microapp, not the creator, or no config info
  if (!showConfigInfo) {
    return null;
  }

  return (
    <InfoCard title="Application Configuration" variant={variant}>
      <Box>
        {configDetails.appType && (
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>App Type</Typography>
            <Box className={classes.value}>
              <Chip
                label={configDetails.appType}
                size="small"
                color={
                  configDetails.appType === 'Hosted' ? 'primary' : 'default'
                }
                className={classes.chip}
              />
            </Box>
          </Box>
        )}

        {configDetails.visibility && (
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>
              <VisibilityIcon
                style={{
                  fontSize: '0.875rem',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              />
              Visibility
            </Typography>
            <Typography className={classes.value}>
              {configDetails.visibility}
              {isAuthorized &&
                configDetails.visibility === 'Custom (Specific Users)' && (
                  <Link
                    component="button"
                    variant="body2"
                    sx={{ ml: 1, verticalAlign: 'baseline' }}
                    onClick={() => navigate('./access')}
                    aria-label="Manage access"
                  >
                    (Manage)
                  </Link>
                )}
            </Typography>
          </Box>
        )}

        {configDetails.requiresVPN && (
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>
              <VpnLockIcon
                style={{
                  fontSize: '0.875rem',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              />
              Requires VPN
            </Typography>
            <Box className={classes.value}>
              <Chip
                label="Yes"
                size="small"
                icon={<VpnLockIcon />}
                color="secondary"
                className={classes.chip}
              />
            </Box>
          </Box>
        )}

        {configDetails.port && (
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>
              <PortIcon
                style={{
                  fontSize: '0.875rem',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              />
              Container Port
            </Typography>
            <Typography className={classes.value}>
              {configDetails.port}
            </Typography>
          </Box>
        )}

        {/* IDP-983: Show environment-specific WIF principals based on deployment status */}
        {configDetails.stagingPrincipal && (
          <>
            <Box className={classes.infoRow}>
              <Typography className={classes.label}>
                <AccountBoxIcon
                  style={{
                    fontSize: '0.875rem',
                    verticalAlign: 'middle',
                    marginRight: '4px',
                  }}
                />
                WIF (Staging)
              </Typography>
              <Typography
                className={classes.value}
                style={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  wordBreak: 'break-all',
                }}
              >
                {configDetails.stagingPrincipal}
              </Typography>
            </Box>
            <Box mb={2} p={1.5} bgcolor="info.lighter" borderRadius={1}>
              <Typography variant="caption" color="textSecondary">
                <strong>Note:</strong> Your app is deployed to staging. To
                access GCP resources (Secret Manager, Cloud Storage, etc.),
                grant the <strong>staging WIF principal</strong> above the
                required IAM roles.{' '}
                <Link
                  href="/docs/default/component/internal-developer-portal/deploy-internal-apps-user-guide#workload-identity-federation-wif"
                  target="_blank"
                  rel="noopener"
                >
                  Learn more about WIF setup
                </Link>
              </Typography>
            </Box>
          </>
        )}
        {configDetails.productionPrincipal && (
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>
              <AccountBoxIcon
                style={{
                  fontSize: '0.875rem',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              />
              WIF (Production)
            </Typography>
            <Typography
              className={classes.value}
              style={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                wordBreak: 'break-all',
              }}
            >
              {configDetails.productionPrincipal}
            </Typography>
          </Box>
        )}

        {configDetails.hasPersistentStorage && (
          <>
            <Divider className={classes.divider} />
            <Typography className={classes.sectionTitle}>
              <StorageIcon
                style={{
                  fontSize: '0.875rem',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              />
              Persistent Storage
            </Typography>

            <Box className={classes.infoRow}>
              <Typography className={classes.label}>Status</Typography>
              <Box className={classes.value}>
                <Chip
                  label="Enabled"
                  size="small"
                  color="primary"
                  className={classes.chip}
                />
              </Box>
            </Box>

            {configDetails.storageSize && (
              <Box className={classes.infoRow}>
                <Typography className={classes.label}>
                  <StorageIcon
                    style={{
                      fontSize: '0.875rem',
                      verticalAlign: 'middle',
                      marginRight: '4px',
                    }}
                  />
                  Storage Size
                </Typography>
                <Typography className={classes.value}>
                  {configDetails.storageSize}
                </Typography>
              </Box>
            )}

            <Box className={classes.infoRow}>
              <Typography className={classes.label}>
                <FolderIcon
                  style={{
                    fontSize: '0.875rem',
                    verticalAlign: 'middle',
                    marginRight: '4px',
                  }}
                />
                Mount Path
              </Typography>
              <Typography className={classes.value}>/data</Typography>
            </Box>
          </>
        )}

        <Divider className={classes.divider} />
        <Typography className={classes.sectionTitle}>Metadata</Typography>

        {(configDetails.owners || configDetails.createdBy) && (
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>
              <PersonIcon
                style={{
                  fontSize: '0.875rem',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              />
              Owners
            </Typography>
            <Typography className={classes.value}>
              {configDetails.owners || configDetails.createdBy || 'Unknown'}
            </Typography>
          </Box>
        )}

        {configDetails.createdAt && (
          <Box className={classes.infoRow}>
            <Typography className={classes.label}>
              <CalendarTodayIcon
                style={{
                  fontSize: '0.875rem',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              />
              Created At
            </Typography>
            <Typography className={classes.value}>
              {new Date(configDetails.createdAt).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          </Box>
        )}

        <Divider className={classes.divider} />

        {showUnregisterButton && (
          <Box mt={2} display="flex" justifyContent="flex-end">
            <UnregisterAppButton entity={entity} />
          </Box>
        )}

        {showViewInArgoCDButton && (
          <Box mt={2} display="flex" justifyContent="flex-end">
            <ViewPodLogsButton />
          </Box>
        )}
      </Box>
    </InfoCard>
  );
};
